import {
  Client,
  messageCallbackType,
  StompHeaders,
  StompSubscription
} from "@stomp/stompjs";
import { useCallback, useEffect, useRef, useState } from "react";

export interface StompBaseConfig {
  url: string | (() => string | Promise<string>);
  connectHeaders?: StompHeaders | (() => Promise<StompHeaders>);
}

export type SubscribeFunction = (
  topic: string,
  onMessage: messageCallbackType
) => void;

export interface ActiveSubscription {
  sub: StompSubscription;
  onMessage: messageCallbackType;
}

export function useStompClient({ connectHeaders, url }: StompBaseConfig) {
  const clientRef = useRef<Client | null>(null);
  const subscriptionsRef = useRef<Map<string, ActiveSubscription>>(new Map());
  const pendingSubscriptionsRef = useRef<Map<string, messageCallbackType>>(
    new Map()
  );
  const retryCountRef = useRef(0);
  const MAX_RETRIES = 3;
  const [connected, setConnected] = useState(false);
  const urlRef = useRef(url);
  urlRef.current = url;
  const connectHeadersRef = useRef(connectHeaders);
  connectHeadersRef.current = connectHeaders;

  const flushPendingSubscriptions = useCallback(() => {
    pendingSubscriptionsRef.current.forEach((onMessage, topic) => {
      if (!clientRef.current?.connected) return;
      if (subscriptionsRef.current.has(topic)) return;
      const sub = clientRef.current.subscribe(topic, onMessage);
      subscriptionsRef.current.set(topic, { sub, onMessage });
    });
    pendingSubscriptionsRef.current.clear();
  }, []);

  const connect = useCallback(async () => {
    if (clientRef.current?.connected || clientRef.current?.active) return;

    const resolvedUrl =
      typeof urlRef.current === "function"
        ? await urlRef.current()
        : urlRef.current;

    if (!resolvedUrl) return;

    const resolvedHeaders =
      typeof connectHeadersRef.current === "function"
        ? await connectHeadersRef.current()
        : connectHeadersRef.current;

    const client = new Client({
      brokerURL: resolvedUrl,
      ...(resolvedHeaders ? { connectHeaders: resolvedHeaders } : {}),
      reconnectDelay: 2000,
      // debug: (msg) => console.log("[STOMP]", msg),
      onConnect: () => {
        // console.log("[STOMP] Connected");
        retryCountRef.current = 0;
        setConnected(true);
        flushPendingSubscriptions();
      },
      onDisconnect: () => {
        // console.log("[STOMP] Disconnected");
        setConnected(false);
      },
      onStompError: (frame) => {
        // console.error("[STOMP ERROR]", frame.body);
        console.error("[STOMP ERROR]", frame);
        subscriptionsRef.current.forEach(({ onMessage }, topic) => {
          pendingSubscriptionsRef.current.set(topic, onMessage);
        });
        subscriptionsRef.current.forEach(({ sub }) => sub.unsubscribe());
        subscriptionsRef.current.clear();
        setConnected(false);

        if (retryCountRef.current < MAX_RETRIES) {
          retryCountRef.current += 1;
          console.warn(
            `[STOMP] Tentando reconectar (${retryCountRef.current}/${MAX_RETRIES})...`
          );
          client.deactivate().then(() => {
            clientRef.current = null;
            connect();
          });
        } else {
          console.error(
            "[STOMP] Limite de tentativas atingido. Encerrando reconexões."
          );
          retryCountRef.current = 0;
        }
      },
      onWebSocketError: (err) => {
        // console.error("[WS ERROR]", err);
        // Recuperar subscribes ativos para a fila antes de desinscrever
        subscriptionsRef.current.forEach(({ onMessage }, topic) => {
          pendingSubscriptionsRef.current.set(topic, onMessage);
        });
        subscriptionsRef.current.forEach(({ sub }) => sub.unsubscribe());
        subscriptionsRef.current.clear();
        setConnected(false);

        if (retryCountRef.current < MAX_RETRIES) {
          retryCountRef.current += 1;
          console.warn(
            `[WS] Tentando reconectar (${retryCountRef.current}/${MAX_RETRIES})...`
          );
          client.deactivate().then(() => {
            clientRef.current = null;
            connect();
          });
        } else {
          console.error(
            "[WS] Limite de tentativas atingido. Encerrando reconexões."
          );
          retryCountRef.current = 0;
        }
      }
    });

    client.activate();
    clientRef.current = client;
  }, [flushPendingSubscriptions]);

  const disconnect = useCallback(() => {
    subscriptionsRef.current.forEach(({ sub }) => sub.unsubscribe());
    subscriptionsRef.current.clear();
    pendingSubscriptionsRef.current.clear();

    clientRef.current?.deactivate();
    clientRef.current = null;
    setConnected(false);
  }, []);

  const subscribe = useCallback<SubscribeFunction>(
    (topic: string, onMessage: messageCallbackType) => {
      if (subscriptionsRef.current.has(topic)) return;

      if (!clientRef.current?.connected) {
        console.warn("[STOMP] Not connected yet, queuing subscription:", topic);
        pendingSubscriptionsRef.current.set(topic, onMessage);
        return;
      }

      const sub = clientRef.current.subscribe(topic, onMessage);
      subscriptionsRef.current.set(topic, { sub, onMessage });
    },
    []
  );

  const unsubscribe = useCallback((topic: string) => {
    subscriptionsRef.current.get(topic)?.sub.unsubscribe();
    subscriptionsRef.current.delete(topic);
    pendingSubscriptionsRef.current.delete(topic);
  }, []);

  // cleanup automático quando o componente morrer
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    connect,
    disconnect,
    subscribe,
    unsubscribe,
    connected,
    client: clientRef.current
  };
}
